import type { PropsWithChildren, ComponentProps } from 'react'
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import styled from 'styled-components'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import type Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import clsx from 'clsx'
export const MIN_MONTH_TAB_BUTTON_WIDTH: number = 108
const SCROLL_ADVANCE = MIN_MONTH_TAB_BUTTON_WIDTH * 3

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`

const Container = styled(HorizontalContainerWithFadedOverflow)`
  gap: ${rem(8)};
  align-items: stretch;
`

const FloatingActionContainer = styled.div`
  display: none;
  position: absolute;
  top: 50%;
  pointer-events: none;
  opacity: 1;
  transition: .2s ease opacity;

  ${mediaQueryUp.tablet} {
    display: block;
  }

  > * {
    pointer-events: initial;
    box-shadow: ${props => props.theme.shadow.bottom.small};
  }

  &.at-start {
    left: 0;
    transform: translateX(-50%) translateY(-50%);
  }

  &.at-end {
    right: 0;
    transform: translateX(50%) translateY(-50%);
  }

  &.is-hidden {
    opacity: 0;

    > * {
      pointer-events: none;
    }
  }
`

interface Props {
  className?: string
  gap?: ComponentProps<typeof Group>['gap']
}

const MonthTabButtonsGroup = forwardRef<HTMLDivElement, PropsWithChildren<Props>>((props, ref) => {
  const {
    children,
    className,
    gap,
  } = props

  const tabsGroupRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(ref, () => tabsGroupRef.current!)

  const scrollBackward = useCallback(() => {
    if (tabsGroupRef.current) {
      tabsGroupRef.current.scrollLeft -= SCROLL_ADVANCE + (tabsGroupRef.current.scrollLeft % SCROLL_ADVANCE)
    }
  }, [])

  const scrollForward = useCallback(() => {
    if (tabsGroupRef.current) {
      tabsGroupRef.current.scrollLeft += SCROLL_ADVANCE + (tabsGroupRef.current.scrollLeft % SCROLL_ADVANCE)
    }
  }, [])

  const [hasReachedStart, setHasReachedStart] = useState<boolean>(false)
  const [hasReachedEnd, setHasReachedEnd] = useState<boolean>(false)

  return <Wrapper className={className}>
    <Container
      ref={tabsGroupRef}
      onHasReachedStart={setHasReachedStart}
      onHasReachedEnd={setHasReachedEnd}
      gap={gap}
    >
      {children}
    </Container>
    <FloatingActionContainer
      className={clsx('at-start', {
        'is-hidden': hasReachedStart,
      })}
    >
      <IconButton
        kind="primary"
        variant="ghost"
        size="small"
        shape="circle"
        onClick={scrollBackward}
      >
        <LineAngleLeftIcon />
      </IconButton>
    </FloatingActionContainer>
    <FloatingActionContainer
      className={clsx('at-end', {
        'is-hidden': hasReachedEnd,
      })}
    >
      <IconButton
        kind="primary"
        size="small"
        variant="ghost"
        shape="circle"
        onClick={scrollForward}
      >
        <LineAngleRightIcon />
      </IconButton>
    </FloatingActionContainer>
  </Wrapper>
})

export default MonthTabButtonsGroup
